import type {
  EnergyBucket,
  EnergyResolution,
  PowerNow,
} from "@energyplatform/client";

// Figures of the overview screen derived from the balance the host calculates; nothing here adds up points into a
// balance of its own (doc 12, section 7).

/** A meter's reading older than this is late: the point is shown with the time of its last value. */
export const LATE_AFTER_MS = 15 * 60_000;

/**
 * Condition sensors (temperature, humidity, light…) are often battery powered and send a value only when it changes
 * by a step: hours of silence are normal, so only a longer one is late (the host's DataFreshness).
 */
export const CONDITION_LATE_AFTER_MS = 6 * 3_600_000;

export type Freshness = "ok" | "late" | "none";

export function freshness(
  at: string | null,
  now: number,
  lateAfterMs = LATE_AFTER_MS,
): Freshness {
  if (at === null) {
    return "none";
  }
  return now - new Date(at).getTime() > lateAfterMs ? "late" : "ok";
}

/** How long a point may stay silent before its data is late: meters minutes, condition sensors hours. */
export function lateAfterFor(point: { role: string }): number {
  return point.role === "environment" ? CONDITION_LATE_AFTER_MS : LATE_AFTER_MS;
}

/** A battery that needs replacing: reported low or empty, or below 15 %. */
export function batteryLow(reading: {
  batteryLevel?: number | null;
  batteryState?: string | null;
}): boolean {
  return (
    reading.batteryState === "low" ||
    reading.batteryState === "empty" ||
    (reading.batteryLevel != null && reading.batteryLevel < 15)
  );
}

/** Start of the bucket after `start`, in the browser's calendar for days and months. */
export function nextBucket(start: Date, resolution: EnergyResolution): Date {
  switch (resolution) {
    case "15m":
      return new Date(start.getTime() + 15 * 60_000);
    case "hour":
      return new Date(start.getTime() + 3_600_000);
    case "day":
      return new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 1,
      );
    case "month":
      return new Date(start.getFullYear(), start.getMonth() + 1, 1);
  }
}

export interface Slot {
  from: number;
  to: number;
  bucket: EnergyBucket | null;
}

/** Every bucket of [from, min(to, now)), each with the host's bucket when there is one. */
export function slots(
  buckets: EnergyBucket[],
  from: Date,
  to: Date,
  now: Date,
  resolution: EnergyResolution,
): Slot[] {
  const end = Math.min(to.getTime(), now.getTime());
  const byStart = buckets.map((b) => ({ at: new Date(b.at).getTime(), b }));
  const out: Slot[] = [];
  for (let s = from; s.getTime() < end;) {
    const n = nextBucket(s, resolution);
    const a = s.getTime();
    const z = n.getTime();
    out.push({
      from: a,
      to: z,
      bucket: byStart.find((x) => x.at >= a && x.at < z)?.b ?? null,
    });
    s = n;
  }
  return out;
}

export interface Completeness {
  /** Share of elapsed buckets with data from every balance point, 0–1; null before the period starts. */
  share: number | null;
  /** Runs of buckets with missing or partial data, merged. */
  gaps: { from: number; to: number }[];
  /** Total length of the gaps in hours. */
  gapHours: number;
}

export function completeness(all: Slot[]): Completeness {
  if (all.length === 0) {
    return { share: null, gaps: [], gapHours: 0 };
  }
  const gaps: { from: number; to: number }[] = [];
  let full = 0;
  for (const s of all) {
    if (s.bucket?.complete) {
      full++;
      continue;
    }
    const last = gaps[gaps.length - 1];
    if (last && last.to === s.from) {
      last.to = s.to;
    } else {
      gaps.push({ from: s.from, to: s.to });
    }
  }
  return {
    share: full / all.length,
    gaps,
    gapHours: gaps.reduce((h, g) => h + (g.to - g.from) / 3_600_000, 0),
  };
}

export interface PowerPoint {
  t: number;
  v: number | null;
  /** End of the bucket (ms), so a peak can be shown as the interval it averages. */
  to?: number;
}

/**
 * Average power (kW) of each bucket from its energy (kWh): only buckets with complete data, the rest are gaps. A
 * bucket still running is a gap too: its energy so far divided by the whole bucket would understate the power.
 */
export function averagePower(
  all: Slot[],
  pick: (b: EnergyBucket) => number | null,
  now: number = Date.now(),
): PowerPoint[] {
  return all.map((s) => {
    const kwh = s.bucket?.complete && s.to <= now ? pick(s.bucket) : null;
    return {
      t: s.from,
      to: s.to,
      v: kwh === null ? null : kwh / ((s.to - s.from) / 3_600_000),
    };
  });
}

export function peak(points: PowerPoint[]): PowerPoint | null {
  let best: PowerPoint | null = null;
  for (const p of points) {
    if (p.v !== null && (best === null || p.v > best.v!)) {
      best = p;
    }
  }
  return best;
}

/** Chart buckets offered for a period: fine ones only where they stay readable. */
export function chartResolutions(from: Date, to: Date): EnergyResolution[] {
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  const out: EnergyResolution[] = [];
  if (days <= 7) {
    out.push("15m");
  }
  if (days <= 62) {
    out.push("hour");
  }
  if (days > 1) {
    out.push("day");
  }
  return out;
}

export function defaultChartResolution(from: Date, to: Date): EnergyResolution {
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  return days <= 2 ? "15m" : days <= 31 ? "hour" : "day";
}

export interface Flow {
  key: string;
  label: string;
  kw: number;
  kind: "grid" | "pv" | "load" | "export" | "rest" | "unmeasured";
  pointId?: string;
}

export interface Flows {
  ins: Flow[];
  outs: Flow[];
  /** Consumption of the node, kW. */
  total: number;
}

/**
 * The balance now as flows: the grid and own production feed the node, which feeds its metered outlets, the grid
 * (export) and the part no point measures. Only with a balance boundary.
 */
export interface FlowLabels {
  grid: string;
  pv: string;
  export: string;
  rest: string;
  /** A source no point measures, or readings of meters taken at different moments. */
  unmeasured?: string;
}

/**
 * What goes out can never exceed what comes in: when the outlets add up to more than the inputs (PV behind a
 * connection meter, or meters read at different moments), the difference is drawn as an unmeasured input.
 */
export function balanced(flows: Flows, label: string): Flows {
  const ins = flows.ins.reduce((s, f) => s + f.kw, 0);
  const outs = flows.outs.reduce((s, f) => s + f.kw, 0);
  const missing = outs - ins;
  return missing > Math.max(0.0005, ins * 0.001)
    ? {
        ...flows,
        ins: [
          ...flows.ins,
          { key: "unmeasured", label, kw: missing, kind: "unmeasured" },
        ],
      }
    : flows;
}

export function flowsNow(now: PowerNow, labels: FlowLabels): Flows | null {
  if (!now.hasBoundary || now.grid === null) {
    return null;
  }
  const production = Math.max(0, now.production ?? 0);
  // Connections each on its own: one may draw while another feeds back.
  const imported = now.import ?? Math.max(0, now.grid);
  const exported = now.export ?? Math.max(0, -now.grid);
  const ins: Flow[] = [
    { key: "grid", label: labels.grid, kw: imported, kind: "grid" },
  ];
  if (now.production !== null) {
    ins.push({ key: "pv", label: labels.pv, kw: production, kind: "pv" });
  }
  const total = Math.max(
    0,
    now.consumption ?? imported + production - exported,
  );
  // The host picks the outlets (sub-meters and loads without overlap); those with a reading are drawn.
  const outlets = new Set(now.outlets);
  const measured = now.points
    .filter((p) => outlets.has(p.pointId) && p.power !== null)
    .sort((a, b) => b.power! - a.power!);
  const loads = measured.map<Flow>((p) => ({
    key: p.pointId,
    label: p.name,
    kw: Math.max(0, p.power!),
    kind: "load",
    pointId: p.pointId,
  }));
  const outs: Flow[] = [...loads];
  if (exported > 0) {
    outs.push({
      key: "export",
      label: labels.export,
      kw: exported,
      kind: "export",
    });
  }
  const rest = total - loads.reduce((s, l) => s + l.kw, 0);
  if (rest > 0.0005) {
    outs.push({ key: "rest", label: labels.rest, kw: rest, kind: "rest" });
  }
  return balanced({ ins, outs, total }, labels.unmeasured ?? labels.rest);
}

/** Sections of the overview: electricity (with PV and storage), each utility medium, and environmental conditions. */
export type OverviewGroup = "el" | "water" | "gas" | "air" | "env";

/** Utility sections shown even without points in the scope, so the place for them is visible. */
export const ALWAYS_SHOWN: readonly OverviewGroup[] = [
  "el",
  "water",
  "gas",
  "env",
];

export function overviewGroup(point: {
  medium: string;
  role: string;
}): OverviewGroup {
  if (point.role === "environment") {
    return "env";
  }
  switch (point.medium) {
    case "el":
    case "pv":
    case "bess":
      return "el";
    case "water":
    case "gas":
    case "air":
      return point.medium;
    default:
      return "env";
  }
}

/** Order of environmental quantities in the summary. */
const CONDITION_ORDER = [
  "temp",
  "humidity",
  "light",
  "pressure",
  "wind",
  "air",
];

export interface ConditionExtreme {
  pointId: string;
  value: number;
}

/**
 * Current conditions of one quantity (medium and unit) in the scope: mean, lowest and highest of the sensors with a
 * current value. Late sensors are counted but never averaged in; a quantity read in two units is two summaries.
 */
export interface ConditionSummary {
  medium: string;
  unit: string | null;
  sensors: number;
  current: number;
  mean: number | null;
  min: ConditionExtreme | null;
  max: ConditionExtreme | null;
}

export function conditionSummary(
  points: readonly { id: string; medium: string; role: string }[],
  readings: ReadonlyMap<
    string,
    { value: number | null; unit: string | null; at: string | null }
  >,
  now: number,
): ConditionSummary[] {
  const groups = new Map<string, ConditionSummary & { sum: number }>();
  for (const p of points) {
    if (overviewGroup(p) !== "env") {
      continue;
    }
    const r = readings.get(p.id);
    const unit = r?.unit ?? null;
    // A sensor without a reading yet joins the summary of its medium whatever unit that has.
    const key =
      r?.unit === undefined || r.unit === null
        ? ([...groups.keys()].find((k) => k.startsWith(`${p.medium}|`)) ??
          `${p.medium}|`)
        : `${p.medium}|${unit}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        medium: p.medium,
        unit,
        sensors: 0,
        current: 0,
        mean: null,
        min: null,
        max: null,
        sum: 0,
      };
      groups.set(key, g);
    }
    g.unit ??= unit;
    g.sensors++;
    if (
      !r ||
      r.value === null ||
      freshness(r.at, now, CONDITION_LATE_AFTER_MS) !== "ok"
    ) {
      continue;
    }
    g.current++;
    g.sum += r.value;
    if (g.min === null || r.value < g.min.value) {
      g.min = { pointId: p.id, value: r.value };
    }
    if (g.max === null || r.value > g.max.value) {
      g.max = { pointId: p.id, value: r.value };
    }
  }
  return [...groups.values()]
    .map(({ sum, ...g }) => ({
      ...g,
      mean: g.current > 0 ? sum / g.current : null,
    }))
    .sort(
      (a, b) =>
        byConditionOrder(a.medium, b.medium) ||
        (a.unit ?? "").localeCompare(b.unit ?? ""),
    );
}

/** Comparer of environmental media: temperature, humidity, light…, then the rest by name. */
export function byConditionOrder(a: string, b: string): number {
  const rank = (m: string) => {
    const i = CONDITION_ORDER.indexOf(m);
    return i < 0 ? CONDITION_ORDER.length : i;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
}
